import { useState, useEffect } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '@/auth/hooks/useAuth';
import api, { ArcheMetadata } from '@/lib/api';

interface ConditionalProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * A conditional route protection component for arches.
 *
 * - Public arches (isPublic=true) can be accessed without authentication
 * - Non-public arches require authentication
 *
 * This component fetches arche metadata to determine if the arche is public
 * before deciding whether to require authentication.
 */
export function ConditionalProtectedRoute({ children }: ConditionalProtectedRouteProps) {
  const { id: archeId } = useParams<{ id: string }>();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const location = useLocation();

  const [archeMetadata, setArcheMetadata] = useState<ArcheMetadata | null>(null);
  const [metadataLoading, setMetadataLoading] = useState(true);
  const [metadataError, setMetadataError] = useState<string | null>(null);

  useEffect(() => {
    if (!archeId) {
      setMetadataLoading(false);
      return;
    }

    let cancelled = false;

    async function fetchMetadata() {
      try {
        const metadata = await api.getArcheMetadata(archeId!);
        if (!cancelled) {
          setArcheMetadata(metadata);
          setMetadataError(null);
        }
      } catch (err) {
        if (!cancelled) {
          // If we get a 404, treat it as not found
          // If we get a 401, the arche exists but requires auth
          setMetadataError(err instanceof Error ? err.message : 'Failed to load arche');
          setArcheMetadata(null);
        }
      } finally {
        if (!cancelled) {
          setMetadataLoading(false);
        }
      }
    }

    fetchMetadata();

    return () => {
      cancelled = true;
    };
  }, [archeId]);

  // Show loading while auth or metadata is being fetched
  if (authLoading || metadataLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // No arche ID provided
  if (!archeId) {
    return <Navigate to="/" replace />;
  }

  // Metadata fetch failed - could be 404 or network error
  if (metadataError) {
    // If not authenticated, redirect to login (the arche might exist but require auth)
    if (!isAuthenticated) {
      return <Navigate to="/auth/login" state={{ from: location.pathname }} replace />;
    }
    // If authenticated but still got an error, show not found
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground">Not Found</h1>
          <p className="mt-2 text-muted-foreground">
            This arche could not be found.
          </p>
        </div>
      </div>
    );
  }

  // If arche is public, allow access regardless of auth state
  if (archeMetadata?.isPublic) {
    return <>{children}</>;
  }

  // Non-public arche requires authentication
  if (!isAuthenticated) {
    return <Navigate to="/auth/login" state={{ from: location.pathname }} replace />;
  }

  return <>{children}</>;
}
